import { ArrowLeft, CalendarCheck, CalendarDays, CalendarX, CreditCard } from "lucide-react";
import { useState, useEffect } from "react";
import { TailSpin } from "react-loading-icons";
import { useSelector } from "react-redux";
import { formatDateInCompanyTimeZone } from "../../../../utils/format";

const FlexCart = ({
  items,
  totalAmount,
  currencySymbol,
  onCheckout,
  onContinueShopping,
  onEmptyCart,
  isLoading,
  isProcessing
}) => {
  const [open] = useState(false);
  const [alertMsg, setAlertMsg] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [autoCheckoutTriggered, setAutoCheckoutTriggered] = useState(false);

  // Get company domain from Redux store
  const companyState = useSelector((state) => state.companyReducer.company);
  const companydomain = companyState?.domain?.type;

  // Auto-trigger checkout for FLEX_OFFICE domain
  useEffect(() => {
    if (companydomain === "FLEX_OFFICE" && 
        items?.length >= 1 && 
        !autoCheckoutTriggered && 
        !isProcessing && 
        !isLoading) {
      setAutoCheckoutTriggered(true);
      onCheckout();
    }
  }, [companydomain, items, autoCheckoutTriggered, isProcessing, isLoading, onCheckout]);

  // Helper Functions
  const formatTime = (timeStr) => {
    const [hour, minute] = timeStr.split(':').map(Number);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    return formatDateInCompanyTimeZone(dateStr, companyState, "MMM d, yyyy", "Invalid Date");
  };

  const getProductImage = (product) => {
    return product?.imageContents?.[0]?.displayLink || "https://via.placeholder.com/150";
  };

  const calculateDuration = (item) => {
    // First try to get duration from productQuantity
    const duration = item?.productQuantity?.numberOfUnit;
    if (duration) return parseInt(duration);

    // Fallback to schedule-based calculation
    const { schedule } = item.scheduledProduct || {};
    if (!schedule) return null;

    const start = new Date(`2000-01-01T${schedule.startTime}`);
    const end = new Date(`2000-01-01T${schedule.endTime}`);
    const diffMs = end - start;
    const hours = Math.round(diffMs / (1000 * 60 * 60));
    return hours;
  };

  const calculateHourlyRate = (item) => {
    // Get the hourly rate from product price
    const pricePerHour = item?.scheduledProduct?.product?.price?.priceAmt ||
      item?.product?.price?.priceAmt;
    if (pricePerHour) return parseFloat(pricePerHour).toFixed(2);

    // Fallback: calculate from total price and duration
    const duration = calculateDuration(item);
    if (!duration || duration === 0) return null;
    const rate = (item.totalPrice || totalAmount) / duration;
    return rate.toFixed(2);
  };

  // Navigation Handlers
  const handleBackNavigation = () => window.history.back();

  // Components
  const AlertComponent = () => (
    alertMsg.open && (
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
        <div className={`px-6 py-4 rounded-xl shadow-2xl backdrop-blur-sm ${alertMsg.severity === 'error' ? 'bg-red-500/90' :
          alertMsg.severity === 'success' ? 'bg-green-500/90' : 'bg-yellow-500/90'
          } text-white`}>
          <div className="flex justify-between items-center">
            <span className="font-medium">{alertMsg.message}</span>
            <button
              onClick={() => setAlertMsg({ ...alertMsg, open: false })}
              className="ml-4 text-xl hover:text-gray-200 transition-colors"
            >
              &times;
            </button>
          </div>
        </div>
      </div>
    )
  );

  const CartHeader = () => (
    <div style={{ paddingTop: '5rem' }}>
      <div className="mb-8 bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl p-8 text-white shadow-xl border border-slate-600">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <div className="flex items-center gap-6">
            <button
              onClick={handleBackNavigation}
              className="p-3 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-300 hover:scale-105 border border-white/20"
            >
              <ArrowLeft className="h-5 w-5 text-white" />
            </button>
            <div id="cart-header-text">
              <h1 id="booking-summary-title" className="text-3xl font-bold mb-2 tracking-wide text-white">
                Booking Summary
              </h1>
              <p id="booking-summary-description" className="text-slate-200 text-base">
                Review your space reservations
              </p>
            </div>
          </div>
          <div className="relative">
            <CalendarCheck className="h-12 w-12 text-blue-400" />
            {items?.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-blue-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shadow-lg">
                {items?.length}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const ScheduleInfo = ({ item }) => {
    const { schedule, period } = item.scheduledProduct;
    if (!schedule || !period) return null;

    const { startTime, endTime } = schedule;
    const { effectiveStartDate, effectiveEndDate } = period;

    return (
      <div id="schedule-info" className="inline-block px-4 py-2 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg mb-3 border border-blue-200">
        <div className="flex items-center gap-2 mb-1">
          <CalendarDays className="h-4 w-4" />
          <span className="font-semibold">Reservation Period</span>
        </div>
        <div className="text-xs text-slate-600">
          {formatDate(effectiveStartDate)} at {formatTime(startTime)} → {formatDate(effectiveEndDate)} at {formatTime(endTime)}
        </div>
      </div>
    );
  };

  const CartItem = ({ item, index }) => {
    const duration = calculateDuration(item);
    const hourlyRate = calculateHourlyRate(item);

    return (
      <div key={index} className="p-6 hover:bg-slate-50 transition-colors duration-200">
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="w-full sm:w-36 h-36 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl overflow-hidden shadow-md border border-slate-200">
            <img
              id="cart-item-image"
              src={getProductImage(item.scheduledProduct?.product || item.product)}
              alt={item.scheduledProduct?.product?.name || item.product?.name}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
          </div>

          <div id="cart-item-details" className="flex-grow">
            <div className="flex flex-col h-full">
              <div id="cart-item-title" className="flex-grow">
                <h3 className="text-xl font-bold text-slate-800 mb-2 hover:text-blue-600 transition-colors">
                  {item.scheduledProduct?.product?.name || item.product?.name}
                </h3>
                <ScheduleInfo item={item} />
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                <div className="text-sm text-slate-600">
                  {duration && hourlyRate && (
                    <span>{duration} {duration === 1 ? 'hour' : 'hours'} × {currencySymbol}{hourlyRate}/hour</span>
                  )}
                </div>
                <span className="text-2xl font-bold text-blue-600">
                  {currencySymbol}{(item.totalPrice || totalAmount).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CartItems = () => (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
      <div className="p-6 border-b border-slate-200 bg-slate-50">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-2xl font-bold text-slate-800">
            Reserved Spaces ({items?.length})
          </h2>
          <button
            id="empty-cart-button"
            onClick={onEmptyCart}
            className="flex items-center gap-2 px-5 py-2.5 border-2 border-red-500 text-red-600 rounded-xl hover:bg-red-50 hover:border-red-600 transition-all duration-300 font-semibold text-sm"
          >
            <CalendarX className="h-4 w-4" />
            Clear All
          </button>
        </div>
      </div>

      <div id="cart-items" className="divide-y divide-slate-100">
        {items.map((item, index) => (
          <CartItem key={index} item={item} index={index} />
        ))}
      </div>
    </div>
  );

  const OrderSummary = () => (
    <div className="sticky top-6">
      <div className="bg-white border-2 border-slate-200 rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-slate-800 p-6 border-b-2 border-slate-700">
          <h2 className="text-2xl font-bold text-center text-white">Booking Summary</h2>
        </div>
        <div className="p-6">
          <p className="text-slate-600 mb-6 leading-relaxed text-sm">
            Review your booking details and proceed with payment to confirm your reservation.
          </p>
          <div className="space-y-3 mb-6">
            <div className="flex justify-between items-center py-2 text-sm border-t border-slate-200 mt-2 pt-3">
              <span className="text-slate-600 font-medium">Booking Details</span>
              <span className="font-semibold text-slate-800 text-right">
                {localStorage.getItem("bookingSummary") || "No booking details"}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 text-sm">
              <span className="text-slate-600 font-medium">Taxes</span>
              <span className="font-semibold text-slate-800">{currencySymbol}0.00</span>
            </div>
            <div className="border-t-2 border-slate-200 my-4"></div>
            <div className="flex justify-between items-center py-3 bg-blue-50 rounded-xl px-4 border border-blue-200">
              <span className="text-lg font-bold text-slate-800">Total Amount</span>
              <span className="text-xl font-bold text-blue-600">
                {currencySymbol}{totalAmount.toFixed(2)}
              </span>
            </div>
          </div>
          <button
            onClick={onCheckout}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 px-6 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] mb-3"
          >
            <div id="checkout-button" className="flex items-center justify-center gap-2">
              <CreditCard className="h-5 w-5" />
              Proceed to Payment
            </div>
          </button>

          <button
            onClick={onContinueShopping}
            className="w-full border-2 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 py-3 px-6 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] text-sm"
          >
            <div className="flex items-center justify-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Browse More Spaces
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const EmptyCart = () => (
    <div id="empty-cart" className="bg-white rounded-2xl p-16 text-center shadow-lg border border-slate-200">
      <div className="max-w-md mx-auto">
        <div className="relative mb-8">
          <CalendarX className="mx-auto h-28 w-28 text-slate-300" />
        </div>
        <h2 className="text-3xl font-bold text-slate-800 mb-4">No Reservations Yet</h2>
        <p className="text-slate-600 mb-8 text-base leading-relaxed">
          You haven't reserved any spaces yet. Browse our available workspaces and meeting rooms to get started.
        </p>
        <button
          onClick={onContinueShopping}
          className="bg-blue-600 hover:bg-blue-700 text-white py-3.5 px-10 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
        >
          <div className="flex items-center justify-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Explore Spaces
          </div>
        </button>
      </div>
    </div>
  );

  const LoadingOverlay = () => (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl p-8 shadow-2xl">
        <TailSpin stroke="var(--flex-primary, #2563EB)" strokeWidth={3} />
        <p className="text-slate-700 font-semibold mt-4 text-center">
          {companydomain === "FLEX_OFFICE" ? "Processing your reservation..." : "Processing payment..."}
        </p>
      </div>
    </div>
  );

  // For FLEX_OFFICE domain with items, show only loading overlay and auto-trigger checkout
  if (companydomain === "FLEX_OFFICE" && items?.length >= 1 && !isLoading) {
    return (
      <div className="flex flex-col min-h-screen font-sans bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <LoadingOverlay />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen font-sans bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      {(isProcessing || (companydomain === "FLEX_OFFICE" && autoCheckoutTriggered)) && <LoadingOverlay />}

      <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {open && (
            <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
              <TailSpin stroke="var(--flex-primary, #2563EB)" strokeWidth={3} />
            </div>
          )}

          <AlertComponent />

          <CartHeader />

          {isLoading ? (
            <div className="flex justify-center items-center py-10">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : items?.length >= 1 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <CartItems />
              </div>
              <div className="lg:col-span-1">
                <OrderSummary />
              </div>
            </div>
          ) : (
            <EmptyCart />
          )}
        </div>
      </main>
    </div>
  );
};

export default FlexCart;
